import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";
import { useFileDownload } from "../../ui/useFileDownload";

/**
 * Параметры запуска публикации, как их собирает экран: режим, окно расписания и
 * отбор сайтов.
 */
export interface StartPublicationParams {
  mode: StartPublicationRequest["mode"];
  // Отсутствует у немедленного запуска: время старта нужно только отложенному
  // режиму, иначе задавать нечего.
  scheduledAt?: string;
  // Отсутствует, когда крайний срок публикации не задан: публикация тогда ничем
  // по времени не ограничена.
  deadlineDate?: string;
  filter: NonNullable<StartPublicationRequest["filter"]> | null;
  targetSiteUrls: string[] | null;
}

/**
 * Тело запроса на деактивацию задачи.
 */
interface DeactivateRequestBody {
  // Отсутствует, когда оператор не написал причину: поле не отправляется вовсе,
  // и бэкенд сохраняет деактивацию без пояснения.
  reason?: string;
}

// Единая форма тела ошибки бэкенда: обработчики читают из него текст сообщения.
interface ErrorBody {
  // Отсутствует, когда бэкенд ответил ошибкой без разобранного тела: сообщение
  // тогда собирается из кода ответа.
  error?: string;
}

type VariationPreview =
  paths["/variations/{variationId}/preview"]["get"]["responses"]["200"]["content"]["application/json"];

type StartPublicationRequest =
  paths["/publications"]["post"]["requestBody"]["content"]["application/json"];

type DeferredPublicationRequest =
  paths["/publications/{publicationId}/deferred"]["post"]["requestBody"]["content"]["application/json"];

type RestartUnfinishedUnitsResponse =
  paths["/tasks/{taskId}/regenerate-failed"]["post"]["responses"]["200"]["content"]["application/json"];

export type ActionState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "success" };

export type RestartUnfinishedUnitsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "success"; retriedCount: number };

export type EnsureIndexingSubmissionsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "success"; createdCount: number };

export type PreviewState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "data"; preview: VariationPreview };

export type PreviewSibling = { id: string; index: number };

export type PreviewContext = {
  currentVariationId: string;
  siblings: PreviewSibling[];
};

export interface UseTaskDetailActionsResult {
  regenerateAnchor: (taskAnchorId: string) => void;
  regenerateAnchorState: ActionState;

  updateUrl: (newUrl: string) => void;
  updateUrlState: ActionState;

  viewVariationPreview: (variationId: string, siblings: PreviewSibling[]) => void;
  closePreview: () => void;
  previewState: PreviewState;
  previewContext: PreviewContext | null;

  stopPublication: (publicationId: string) => void;
  stopPublicationState: ActionState;

  resumePublication: (publicationId: string) => void;
  resumePublicationState: ActionState;

  rescheduleDeferred: (publicationId: string, newScheduledAt: string) => void;
  rescheduleDeferredState: ActionState;

  cancelDeferred: (publicationId: string) => void;
  cancelDeferredState: ActionState;

  retryFailedItems: (publicationId: string) => void;
  retryFailedItemsState: ActionState;

  restartUnfinishedUnits: () => void;
  restartUnfinishedUnitsState: RestartUnfinishedUnitsState;
  resetRestartUnfinishedUnitsState: () => void;

  ensureIndexingSubmissions: () => void;
  ensureIndexingSubmissionsState: EnsureIndexingSubmissionsState;
  resetEnsureIndexingSubmissionsState: () => void;

  startPublication: (params: StartPublicationParams) => void;
  startPublicationState: ActionState;

  exportPublicationResults: () => void;
  exportState: ActionState;

  downloadLog: () => void;
  downloadLogState: ActionState;

  deactivate: (reason?: string) => void;
  deactivateState: ActionState;

  activate: () => void;
  activateState: ActionState;
}

export function useTaskDetailActions(): UseTaskDetailActionsResult {
  const { id: taskId } = useParams<{ id: string }>();
  const downloadFile = useFileDownload();

  const [regenerateAnchorState, setRegenerateAnchorState] = useState<ActionState>({ kind: "idle" });
  const [updateUrlState, setUpdateUrlState] = useState<ActionState>({ kind: "idle" });
  const [previewState, setPreviewState] = useState<PreviewState>({ kind: "idle" });
  const [previewContext, setPreviewContext] = useState<PreviewContext | null>(null);
  const [stopPublicationState, setStopPublicationState] = useState<ActionState>({ kind: "idle" });
  const [resumePublicationState, setResumePublicationState] = useState<ActionState>({ kind: "idle" });
  const [rescheduleDeferredState, setRescheduleDeferredState] = useState<ActionState>({ kind: "idle" });
  const [cancelDeferredState, setCancelDeferredState] = useState<ActionState>({ kind: "idle" });
  const [retryFailedItemsState, setRetryFailedItemsState] = useState<ActionState>({ kind: "idle" });
  const [restartUnfinishedUnitsState, setRestartUnfinishedUnitsState] = useState<RestartUnfinishedUnitsState>({
    kind: "idle",
  });
  const [ensureIndexingSubmissionsState, setEnsureIndexingSubmissionsState] = useState<EnsureIndexingSubmissionsState>({
    kind: "idle",
  });
  const [startPublicationState, setStartPublicationState] = useState<ActionState>({ kind: "idle" });
  const [exportState, setExportState] = useState<ActionState>({ kind: "idle" });
  const [downloadLogState, setDownloadLogState] = useState<ActionState>({ kind: "idle" });
  const [deactivateState, setDeactivateState] = useState<ActionState>({ kind: "idle" });
  const [activateState, setActivateState] = useState<ActionState>({ kind: "idle" });

  const regenerateAnchor = useCallback(
    (unitId: string) => {
      setRegenerateAnchorState({ kind: "loading" });

      fetchWithRefresh(`/api/tasks/${taskId}/units/${unitId}/regenerate`, {
        method: "POST",
      })
        .then(async (response) => {
          if (response.ok) {
            // Успех подтверждает тело ответа, а не только код: контракт кладёт
            // причину отказа в поле `error`, и оно сильнее кода состояния.
            const body = (await response.json()) as ErrorBody;
            setRegenerateAnchorState(
              body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
            );
          } else {
            const body = (await response.json()) as ErrorBody;
            setRegenerateAnchorState({ kind: "error", message: body.error ?? "Ошибка перегенерации" });
          }
        })
        .catch(() => {
          setRegenerateAnchorState({ kind: "error", message: "Ошибка сети" });
        });
    },
    [taskId],
  );

  const updateUrl = useCallback(
    (newUrl: string) => {
      setUpdateUrlState({ kind: "loading" });

      fetchWithRefresh(`/api/tasks/${taskId}/url`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newTargetUrl: newUrl }),
      })
        .then(async (response) => {
          if (response.ok) {
            // Успех подтверждает тело ответа, а не только код: контракт кладёт
            // причину отказа в поле `error`, и оно сильнее кода состояния.
            const body = (await response.json()) as ErrorBody;
            setUpdateUrlState(
              body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
            );
          } else {
            const body = (await response.json()) as ErrorBody;
            setUpdateUrlState({ kind: "error", message: body.error ?? "Ошибка обновления URL" });
          }
        })
        .catch(() => {
          setUpdateUrlState({ kind: "error", message: "Ошибка сети" });
        });
    },
    [taskId],
  );

  const viewVariationPreview = useCallback((variationId: string, siblings: PreviewSibling[]) => {
    setPreviewContext({ currentVariationId: variationId, siblings });
    setPreviewState({ kind: "loading" });

    fetchWithRefresh(`/api/variations/${variationId}/preview`)
      .then(async (response) => {
        if (response.ok) {
          const data = (await response.json()) as VariationPreview;
          setPreviewState({ kind: "data", preview: data });
        } else {
          const body = (await response.json()) as ErrorBody;
          setPreviewState({ kind: "error", message: body.error ?? "Ошибка загрузки превью" });
        }
      })
      .catch(() => {
        setPreviewState({ kind: "error", message: "Ошибка сети" });
      });
  }, []);

  const closePreview = useCallback(() => {
    setPreviewState({ kind: "idle" });
    setPreviewContext(null);
  }, []);

  const stopPublication = useCallback((publicationId: string) => {
    setStopPublicationState({ kind: "loading" });

    fetchWithRefresh(`/api/publications/${publicationId}/stop`, {
      method: "POST",
    })
      .then(async (response) => {
        if (response.ok) {
          // Успех подтверждает тело ответа, а не только код: контракт кладёт
          // причину отказа в поле `error`, и оно сильнее кода состояния.
          const body = (await response.json()) as ErrorBody;
          setStopPublicationState(
            body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
          );
        } else {
          const body = (await response.json()) as ErrorBody;
          setStopPublicationState({ kind: "error", message: body.error ?? "Ошибка остановки" });
        }
      })
      .catch(() => {
        setStopPublicationState({ kind: "error", message: "Ошибка сети" });
      });
  }, []);

  const resumePublication = useCallback((publicationId: string) => {
    setResumePublicationState({ kind: "loading" });

    fetchWithRefresh(`/api/publications/${publicationId}/resume`, {
      method: "POST",
    })
      .then(async (response) => {
        if (response.ok) {
          // Успех подтверждает тело ответа, а не только код: контракт кладёт
          // причину отказа в поле `error`, и оно сильнее кода состояния.
          const body = (await response.json()) as ErrorBody;
          setResumePublicationState(
            body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
          );
        } else {
          const body = (await response.json()) as ErrorBody;
          setResumePublicationState({ kind: "error", message: body.error ?? "Ошибка возобновления" });
        }
      })
      .catch(() => {
        setResumePublicationState({ kind: "error", message: "Ошибка сети" });
      });
  }, []);

  const rescheduleDeferred = useCallback((publicationId: string, newScheduledAt: string) => {
    setRescheduleDeferredState({ kind: "loading" });

    // datetime-local несёт локальное время пользователя без зоны — отправляем явный инстант
    const body: DeferredPublicationRequest = { action: "reschedule", newScheduledAt: new Date(newScheduledAt).toISOString() };

    fetchWithRefresh(`/api/publications/${publicationId}/deferred`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (response.ok) {
          // Успех подтверждает тело ответа, а не только код: контракт кладёт
          // причину отказа в поле `error`, и оно сильнее кода состояния.
          const body = (await response.json()) as ErrorBody;
          setRescheduleDeferredState(
            body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
          );
        } else {
          const errorBody = (await response.json()) as ErrorBody;
          logger.error(
            { err: errorBody.error ?? `HTTP ${response.status}`, entityType: "publication", entityId: publicationId, step: "reschedule" },
            "Failed to reschedule deferred publication",
          );
          setRescheduleDeferredState({ kind: "error", message: errorBody.error ?? "Ошибка переноса публикации" });
        }
      })
      .catch((error: unknown) => {
        logger.error(
          { err: error, entityType: "publication", entityId: publicationId, step: "reschedule" },
          "Failed to reschedule deferred publication",
        );
        setRescheduleDeferredState({ kind: "error", message: "Ошибка сети" });
      });
  }, []);

  const cancelDeferred = useCallback((publicationId: string) => {
    setCancelDeferredState({ kind: "loading" });

    const body: DeferredPublicationRequest = { action: "cancel" };

    fetchWithRefresh(`/api/publications/${publicationId}/deferred`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (response.ok) {
          // Успех подтверждает тело ответа, а не только код: контракт кладёт
          // причину отказа в поле `error`, и оно сильнее кода состояния.
          const body = (await response.json()) as ErrorBody;
          setCancelDeferredState(
            body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
          );
        } else {
          const errorBody = (await response.json()) as ErrorBody;
          logger.error(
            { err: errorBody.error ?? `HTTP ${response.status}`, entityType: "publication", entityId: publicationId, step: "cancel" },
            "Failed to cancel deferred publication",
          );
          setCancelDeferredState({ kind: "error", message: errorBody.error ?? "Ошибка отмены публикации" });
        }
      })
      .catch((error: unknown) => {
        logger.error(
          { err: error, entityType: "publication", entityId: publicationId, step: "cancel" },
          "Failed to cancel deferred publication",
        );
        setCancelDeferredState({ kind: "error", message: "Ошибка сети" });
      });
  }, []);

  const startPublication = useCallback(
    (parameters: StartPublicationParams) => {
      setStartPublicationState({ kind: "loading" });

      const body: StartPublicationRequest = {
        taskId: taskId as string,
        mode: parameters.mode,
        autoStarted: false,
        // Обе даты контракт требует назвать всегда: немедленная публикация шлёт явный null, а не
        // молчит о поле. datetime-local несёт локальное время пользователя без зоны — отправляем
        // явный инстант, иначе бэкенд интерпретирует строку в часовом поясе сервера.
        scheduledAt:
          parameters.scheduledAt === undefined ? null : new Date(parameters.scheduledAt).toISOString(),
        deadlineDate: parameters.deadlineDate ?? null,
        filter: parameters.filter,
        targetSiteUrls: parameters.targetSiteUrls,
      };

      fetchWithRefresh("/api/publications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
        .then(async (response) => {
          if (response.ok) {
            // Успех подтверждает тело ответа, а не только код: контракт кладёт
            // причину отказа в поле `error`, и оно сильнее кода состояния.
            const body = (await response.json()) as ErrorBody;
            setStartPublicationState(
              body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
            );
          } else {
            const errorBody = (await response.json()) as ErrorBody;
            setStartPublicationState({ kind: "error", message: errorBody.error ?? "Ошибка запуска публикации" });
          }
        })
        .catch(() => {
          setStartPublicationState({ kind: "error", message: "Ошибка сети" });
        });
    },
    [taskId],
  );

  const ensureIndexingSubmissions = useCallback(() => {
    setEnsureIndexingSubmissionsState({ kind: "loading" });

    fetchWithRefresh(`/api/tasks/${taskId}/indexing/ensure`, {
      method: "POST",
    })
      .then(async (response) => {
        if (response.ok) {
          const body = (await response.json()) as { createdCount: number; enqueuedCount: number };
          setEnsureIndexingSubmissionsState({ kind: "success", createdCount: body.createdCount });
        } else {
          const body = (await response.json()) as ErrorBody;
          setEnsureIndexingSubmissionsState({
            kind: "error",
            message: body.error ?? "Ошибка постановки в очередь индексации",
          });
        }
      })
      .catch(() => {
        setEnsureIndexingSubmissionsState({ kind: "error", message: "Ошибка сети" });
      });
  }, [taskId]);

  const resetEnsureIndexingSubmissionsState = useCallback(() => {
    setEnsureIndexingSubmissionsState({ kind: "idle" });
  }, []);

  const retryFailedItems = useCallback((publicationId: string) => {
    setRetryFailedItemsState({ kind: "loading" });

    fetchWithRefresh(`/api/publications/${publicationId}/retry-failed`, {
      method: "POST",
    })
      .then(async (response) => {
        if (response.ok) {
          // Успех подтверждает тело ответа, а не только код: контракт кладёт
          // причину отказа в поле `error`, и оно сильнее кода состояния.
          const body = (await response.json()) as ErrorBody;
          setRetryFailedItemsState(
            body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
          );
        } else {
          const body = (await response.json()) as ErrorBody;
          setRetryFailedItemsState({ kind: "error", message: body.error ?? "Ошибка повтора публикаций" });
        }
      })
      .catch(() => {
        setRetryFailedItemsState({ kind: "error", message: "Ошибка сети" });
      });
  }, []);

  const restartUnfinishedUnits = useCallback(() => {
    setRestartUnfinishedUnitsState({ kind: "loading" });

    fetchWithRefresh(`/api/tasks/${taskId}/regenerate-failed`, {
      method: "POST",
    })
      .then(async (response) => {
        if (response.ok) {
          const body = (await response.json()) as RestartUnfinishedUnitsResponse;
          setRestartUnfinishedUnitsState({ kind: "success", retriedCount: body.retriedCount });
        } else {
          const body = (await response.json()) as ErrorBody;
          setRestartUnfinishedUnitsState({ kind: "error", message: body.error ?? "Ошибка перегенерации" });
        }
      })
      .catch(() => {
        setRestartUnfinishedUnitsState({ kind: "error", message: "Ошибка сети" });
      });
  }, [taskId]);

  const resetRestartUnfinishedUnitsState = useCallback(() => {
    setRestartUnfinishedUnitsState({ kind: "idle" });
  }, []);

  const exportPublicationResults = useCallback(() => {
    setExportState({ kind: "loading" });

    fetchWithRefresh(`/api/tasks/${taskId}/publication/export`)
      .then(async (response) => {
        if (response.ok) {
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition");
          const fileNameMatch = contentDisposition ? contentDisposition.match(/filename="(.+)"/) : null;
          const fileName = fileNameMatch ? fileNameMatch[1] : "export.csv";
          downloadFile(blob, fileName);
          setExportState({ kind: "success" });
        } else {
          const body = (await response.json()) as ErrorBody;
          setExportState({ kind: "error", message: body.error ?? "Ошибка экспорта" });
        }
      })
      .catch(() => {
        setExportState({ kind: "error", message: "Ошибка сети" });
      });
  }, [taskId]);

  const downloadLog = useCallback(() => {
    setDownloadLogState({ kind: "loading" });

    fetchWithRefresh(`/api/tasks/${taskId}/log`)
      .then(async (response) => {
        if (response.ok) {
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition");
          const fileNameMatch = contentDisposition ? contentDisposition.match(/filename="(.+)"/) : null;
          const fileName = fileNameMatch ? fileNameMatch[1] : `task-${taskId}-log.txt`;
          downloadFile(blob, fileName);
          setDownloadLogState({ kind: "success" });
        } else {
          const body = (await response.json()) as ErrorBody;
          setDownloadLogState({ kind: "error", message: body.error ?? "Ошибка скачивания лога" });
        }
      })
      .catch(() => {
        setDownloadLogState({ kind: "error", message: "Ошибка сети" });
      });
  }, [taskId]);

  const deactivate = useCallback(
    (reason?: string) => {
      setDeactivateState({ kind: "loading" });

      const body: DeactivateRequestBody = {};
      if (reason !== undefined && reason !== "") {
        body.reason = reason;
      }

      fetchWithRefresh(`/api/tasks/${taskId}/deactivate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
        .then(async (response) => {
          if (response.ok) {
            // Успех подтверждает тело ответа, а не только код: контракт кладёт
            // причину отказа в поле `error`, и оно сильнее кода состояния.
            const body = (await response.json()) as ErrorBody;
            setDeactivateState(
              body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
            );
          } else {
            const errorBody = (await response.json()) as ErrorBody;
            setDeactivateState({ kind: "error", message: errorBody.error ?? "Ошибка деактивации" });
          }
        })
        .catch(() => {
          setDeactivateState({ kind: "error", message: "Ошибка сети" });
        });
    },
    [taskId],
  );

  const activate = useCallback(() => {
    setActivateState({ kind: "loading" });

    fetchWithRefresh(`/api/tasks/${taskId}/activate`, {
      method: "POST",
    })
      .then(async (response) => {
        if (response.ok) {
          // Успех подтверждает тело ответа, а не только код: контракт кладёт
          // причину отказа в поле `error`, и оно сильнее кода состояния.
          const body = (await response.json()) as ErrorBody;
          setActivateState(
            body.error === undefined ? { kind: "success" } : { kind: "error", message: body.error },
          );
        } else {
          const errorBody = (await response.json()) as ErrorBody;
          setActivateState({ kind: "error", message: errorBody.error ?? "Ошибка активации" });
        }
      })
      .catch(() => {
        setActivateState({ kind: "error", message: "Ошибка сети" });
      });
  }, [taskId]);

  return {
    regenerateAnchor,
    regenerateAnchorState,
    updateUrl,
    updateUrlState,
    viewVariationPreview,
    closePreview,
    previewState,
    previewContext,
    stopPublication,
    stopPublicationState,
    resumePublication,
    resumePublicationState,
    rescheduleDeferred,
    rescheduleDeferredState,
    cancelDeferred,
    cancelDeferredState,
    retryFailedItems,
    retryFailedItemsState,
    restartUnfinishedUnits,
    restartUnfinishedUnitsState,
    resetRestartUnfinishedUnitsState,
    ensureIndexingSubmissions,
    ensureIndexingSubmissionsState,
    resetEnsureIndexingSubmissionsState,
    startPublication,
    startPublicationState,
    exportPublicationResults,
    exportState,
    downloadLog,
    downloadLogState,
    deactivate,
    deactivateState,
    activate,
    activateState,
  };
}
